import React, { useState, useEffect, useCallback, useRef } from 'react';

export interface PaginatedState<T> {
  data: T[];
  page: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  loading: boolean;
  error: string;
  fetch: (page: number) => Promise<void>;
  setPage: (page: number) => void;
  handlePageChange: (newPage: number, sectionSelector?: string) => void;
  getPageNumbers: () => number[];
  setData: React.Dispatch<React.SetStateAction<T[]>>;
  resetPagination: () => void;
  reset: () => void;
}

interface UsePaginatedFetchOptions<T, R> {
  fetchUrl: (page: number, limit: number) => string;
  perPage: number;
  token: string;
  extractData: (response: R) => T[];
  inferHasNext?: boolean;
  totalBased?: {
    extractTotal: (response: R) => number;
    extractHasNext: (response: R) => boolean;
    extractHasPrev?: (response: R) => boolean;
  };
  onResponse?: (response: R) => void;
  parse422?: (raw: any) => string;
  enabled?: boolean;
}

export function usePaginatedFetch<T, R = any>(
  options: UsePaginatedFetchOptions<T, R>
): PaginatedState<T> {
  const {
    token,
    enabled = true,
  } = options;

  const [data, setData] = useState<T[]>([]);
  const [page, setPageState] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Store the latest options in a ref so fetchPage is stable and does not
  // trigger the data-fetch effect on every render (which caused an infinite
  // loop when callers passed inline arrow functions as options).
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const fetchPage = useCallback(async (pageNum: number) => {
    const opts = optionsRef.current;
    if (!opts.token) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(opts.fetchUrl(pageNum, opts.perPage), {
        method: 'GET',
        headers: {
          Accept: '*/*',
          Authorization: `Bearer ${opts.token}`,
        },
      });

      if (response.ok) {
        const raw: R = await response.json();
        const items = opts.extractData(raw);
        setData(items);
        opts.onResponse?.(raw);

        if (opts.totalBased) {
          const total = opts.totalBased.extractTotal(raw);
          const hasNext = opts.totalBased.extractHasNext(raw);
          const hasPrev = opts.totalBased.extractHasPrev ? opts.totalBased.extractHasPrev(raw) : pageNum > 1;
          setHasNextPage(hasNext);
          setHasPrevPage(hasPrev);
          setTotalPages(Math.max(1, Math.ceil(total / opts.perPage)));
        } else if (opts.inferHasNext) {
          const full = items.length === opts.perPage;
          setHasNextPage(full);
          setTotalPages(prev => Math.max(prev, pageNum + (full ? 1 : 0)));
        }
      } else if (response.status === 401 || response.status === 403) {
        // Admin session expired — clear admin tokens and redirect to login
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminEmail');
        localStorage.removeItem('adminRole');
        localStorage.removeItem('adminPermissions');
        window.dispatchEvent(new Event('adminSessionExpired'));
        setError('Your admin session has expired. Please log in again.');
      } else if (response.status === 422) {
        const errData = await response.json().catch(() => ({}));
        const messages = opts.parse422
          ? opts.parse422(errData)
          : Array.isArray(errData.detail)
            ? errData.detail.map((d: { msg: string }) => d.msg).join('. ')
            : 'Invalid request.';
        setError(messages);
      } else {
        setError(`Failed to load (${response.status}). Please try again.`);
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Only fetch when page, token, or enabled changes — NOT when inline option
  // functions change (which happens every render and caused the infinite loop).
  useEffect(() => {
    if (enabled && token) {
      fetchPage(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, token, page]);

  const setPage = useCallback((newPage: number) => {
    setPageState(newPage);
  }, []);

  const handlePageChange = useCallback((newPage: number, sectionSelector?: string) => {
    setPageState(newPage);
    if (sectionSelector) {
      const section = document.querySelector(sectionSelector);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, []);

  const getPageNumbers = useCallback((): number[] => {
    const delta = 2;
    const range: number[] = [];
    const left = Math.max(1, page - delta);
    const right = Math.min(totalPages, page + delta);
    for (let i = left; i <= right; i++) {
      range.push(i);
    }
    return range;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalPages]);

  const resetPagination = useCallback(() => {
    setPageState(1);
    setTotalPages(1);
    setHasNextPage(false);
    setHasPrevPage(false);
  }, []);

  const reset = useCallback(() => {
    setData([]);
    setPageState(1);
    setTotalPages(1);
    setHasNextPage(false);
    setHasPrevPage(false);
    setError('');
  }, []);

  return {
    data,
    page,
    totalPages,
    hasNextPage,
    hasPrevPage,
    loading,
    error,
    fetch: fetchPage,
    setPage,
    handlePageChange,
    getPageNumbers,
    setData,
    resetPagination,
    reset,
  };
}
